const DEFAULT_STREAM_ID = 1;

/**
 * Portal executeSurveillanceCommand body for playback stream creation.
 *
 * Time contract (orca playback-spec / portal playback.js):
 * - UI calendar & timeline use the browser's **local** timezone
 * - API `startTime` / `endTime` are integer **Unix seconds** (timezone-agnostic)
 * - Convert with Math.floor(localDate.getTime() / 1000), never UTC getters for day bounds
 */
export function buildCreatePlaybackStreamCommandPayload({
  streamId = DEFAULT_STREAM_ID,
  startTime,
  endTime,
} = {}) {
  return {
    data: {
      command: "createPlaybackStream",
      params: {
        streamId,
        startTime: Number(startTime),
        endTime: Number(endTime),
      },
    },
  };
}

/**
 * Parse createPlaybackStream success payload.
 *
 * Expected (Falcon wraps under result.data):
 * {
 *   result: {
 *     status: "success",
 *     data: {
 *       protocol: "multipart-replace",
 *       sessionId: "...",
 *       streamUrl: "https://…/playback/stream/…"
 *     }
 *   }
 * }
 */
export function extractPlaybackStreamInfo(response) {
  if (!response) return null;

  const data =
    response.result?.data ||
    response.data ||
    response.result ||
    null;
  if (!data || typeof data !== "object") return null;

  const streamUrl =
    (typeof data.streamUrl === "string" && data.streamUrl.trim()) ||
    (typeof data.relay?.clientStreamUrl === "string" &&
      data.relay.clientStreamUrl.trim()) ||
    "";
  const sessionId =
    (typeof data.sessionId === "string" && data.sessionId.trim()) || "";
  const protocol =
    (typeof data.protocol === "string" && data.protocol.trim()) || "";

  if (!streamUrl || !sessionId || protocol !== "multipart-replace") {
    return null;
  }

  return { streamUrl, sessionId, protocol };
}

export function getCreatePlaybackStreamCommandError(response) {
  if (!response) return "Empty createPlaybackStream response";

  const apiError = response.error || response.result?.error;
  if (apiError?.message) return apiError.message;

  const status = response.result?.status;
  if (status && status !== "success") {
    return response.result?.message || `createPlaybackStream failed (${status})`;
  }

  if (!extractPlaybackStreamInfo(response)) {
    return "createPlaybackStream response missing multipart streamUrl";
  }

  return null;
}
